// Asbestos record side-panel for the FloorCanvas — presence pin, location, material
// details, risk rating, photos, documents, and inspection history.
import { X, Trash2 } from "lucide-react";
import { STATUS_STROKE, ASBESTOS_TYPES, ASBESTOS_CONDITIONS, ASBESTOS_RISK_RATINGS, ASBESTOS_ACTIONS, RISK_COLOR } from "./constants";
import { ZoneDocuments } from "./ZoneDocuments";
import { ZonePhotos } from "./ZonePhotos";
import { ZoneInspectionHistory } from "./ZoneInspectionHistory";

function Labeled({ label, children }) {
    return (
        <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold mb-1" style={{ color: "#9A968F" }}>{label}</div>
            {children}
        </div>
    );
}

export function ZoneDetailPanel({ zone, canEdit, documents, buildingId, clientId,
                                   onChange, onLocalChange, onDelete, onClose, onOpenDoc, onDocsRefresh }) {
    const ro = !canEdit;
    const field = "w-full text-[12px] rounded-[7px] border px-2 py-1.5 bg-white disabled:bg-[#F6F4F0]";
    const sel = (v) => v || "";
    return (
        <div data-testid="asbestos-zone-detail" className="absolute z-30 top-3 right-3 w-[310px] max-h-[calc(70vh-24px)] overflow-auto rounded-[12px] shadow-xl bg-white"
            style={{ border: "1px solid #E2DDD5" }}>
            <div className="flex items-center justify-between px-3.5 py-2.5 border-b sticky top-0 bg-white z-10" style={{ borderColor: "#F0EDE8" }}>
                <div className="text-[12.5px] font-semibold text-[#1F1F1F] inline-flex items-center gap-1.5">
                    <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: STATUS_STROKE[zone.status] }} />
                    Asbestos record
                </div>
                <button data-testid="asbestos-zone-detail-close" onClick={onClose} className="w-6 h-6 grid place-items-center rounded hover:bg-[#F0EDE8]"><X className="w-4 h-4" style={{ color: "#6F6A64" }} /></button>
            </div>
            <div className="p-3.5 space-y-3">
                <div>
                    <div className="text-[10.5px] uppercase tracking-wider font-bold mb-1" style={{ color: "#9A968F" }}>Presence status</div>
                    <div className="grid grid-cols-3 gap-1.5">
                        {[["red", "Present"], ["green", "None"], ["unknown", "Pending"]].map(([s, lbl]) => (
                            <button key={s} data-testid={`asbestos-status-${s}`} disabled={ro} onClick={() => onChange({ status: s })}
                                className="text-[11px] py-1.5 rounded-[7px] border font-medium disabled:opacity-60"
                                style={zone.status === s ? { background: STATUS_STROKE[s], color: "#fff", borderColor: STATUS_STROKE[s] } : { borderColor: "#E2DDD5", color: "#6F6A64" }}>
                                {lbl}
                            </button>
                        ))}
                    </div>
                </div>
                <Labeled label="Label">
                    <input data-testid="asbestos-zone-label" className={field} disabled={ro} value={zone.label || ""}
                        onChange={(e) => onChange({ label: e.target.value })} placeholder="e.g. Boiler room lagging" />
                </Labeled>
                <Labeled label="Reference number">
                    <input data-testid="asbestos-zone-ref" className={field} disabled={ro} value={zone.reference_id || ""}
                        onChange={(e) => onChange({ reference_id: e.target.value })} placeholder="e.g. ACM-014" />
                </Labeled>

                <div className="grid grid-cols-2 gap-2">
                    <Labeled label="Room">
                        <input data-testid="asbestos-zone-room" className={field} disabled={ro} value={zone.room || ""}
                            onChange={(e) => onChange({ room: e.target.value })} placeholder="e.g. Plant room 2" />
                    </Labeled>
                    <Labeled label="Specific location">
                        <input data-testid="asbestos-zone-specific-location" className={field} disabled={ro} value={zone.specific_location || ""}
                            onChange={(e) => onChange({ specific_location: e.target.value })} placeholder="e.g. Above ceiling" />
                    </Labeled>
                </div>

                <Labeled label="Material / product type">
                    <input data-testid="asbestos-zone-material" className={field} disabled={ro} value={zone.material_type || ""}
                        onChange={(e) => onChange({ material_type: e.target.value })} placeholder="e.g. Pipe lagging, AIB ceiling tile" />
                </Labeled>

                <div className="grid grid-cols-2 gap-2">
                    <Labeled label="Asbestos type">
                        <select data-testid="asbestos-zone-type" className={field} disabled={ro} value={sel(zone.asbestos_type)}
                            onChange={(e) => onChange({ asbestos_type: e.target.value || null })}>
                            <option value="">— unknown —</option>
                            {ASBESTOS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Labeled>
                    <Labeled label="Condition">
                        <select data-testid="asbestos-zone-condition" className={field} disabled={ro} value={sel(zone.condition)}
                            onChange={(e) => onChange({ condition: e.target.value || null })}>
                            <option value="">— not set —</option>
                            {ASBESTOS_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Labeled>
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <Labeled label="Risk rating">
                        <select data-testid="asbestos-zone-risk" className={field} disabled={ro} value={sel(zone.risk_rating)}
                            onChange={(e) => onChange({ risk_rating: e.target.value || null })}
                            style={zone.risk_rating ? { color: RISK_COLOR[zone.risk_rating], fontWeight: 600 } : undefined}>
                            <option value="">— not assessed —</option>
                            {ASBESTOS_RISK_RATINGS.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </Labeled>
                    <Labeled label="Survey date">
                        <input type="date" data-testid="asbestos-zone-survey-date" className={field} disabled={ro} value={zone.survey_date || ""}
                            onChange={(e) => onChange({ survey_date: e.target.value || null })} />
                    </Labeled>
                </div>

                <Labeled label="Quantity / extent">
                    <input data-testid="asbestos-zone-quantity" className={field} disabled={ro} value={zone.quantity_extent || ""}
                        onChange={(e) => onChange({ quantity_extent: e.target.value })} placeholder="e.g. Approx 5m² · 2m pipe run" />
                </Labeled>

                <Labeled label="Recommended action">
                    <select data-testid="asbestos-zone-action" className={field} disabled={ro} value={sel(zone.recommended_action)}
                        onChange={(e) => onChange({ recommended_action: e.target.value || null })}>
                        <option value="">— not set —</option>
                        {ASBESTOS_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                </Labeled>

                <Labeled label="Notes">
                    <textarea data-testid="asbestos-zone-notes" rows={3} className={field} disabled={ro} value={zone.notes || ""}
                        onChange={(e) => onChange({ notes: e.target.value })} placeholder="Findings, hover text, etc." />
                </Labeled>

                <Labeled label="Hover text">
                    <input data-testid="asbestos-zone-hover" className={field} disabled={ro} value={zone.hover_text || ""}
                        onChange={(e) => onChange({ hover_text: e.target.value })} placeholder="Short tooltip summary" />
                </Labeled>

                <hr style={{ borderColor: "#F0EDE8" }} />

                <ZoneDocuments zone={zone} canEdit={canEdit} documents={documents} buildingId={buildingId} clientId={clientId}
                    onChange={onChange} onOpenDoc={onOpenDoc} onDocsRefresh={onDocsRefresh} />

                <hr style={{ borderColor: "#F0EDE8" }} />

                <ZonePhotos zone={zone} canEdit={canEdit} buildingId={buildingId} onLocalChange={onLocalChange} />

                <hr style={{ borderColor: "#F0EDE8" }} />

                <ZoneInspectionHistory zone={zone} canEdit={canEdit} onLocalChange={onLocalChange} />

                {canEdit && (
                    <button data-testid="asbestos-zone-delete" onClick={onDelete}
                        className="w-full text-[11.5px] inline-flex items-center justify-center gap-1.5 py-1.5 rounded-[7px] text-white" style={{ background: "#B33A2E" }}>
                        <Trash2 className="w-3.5 h-3.5" /> Delete record
                    </button>
                )}
            </div>
        </div>
    );
}
